import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/lib/api';
import { contractTemplateSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { authenticatedFetch } from '@/lib/authenticated-fetch';

interface MergeVariable {
  key: string;
  label: string;
  example: string;
  group?: string;
}

interface TemplateDetail {
  id: string;
  title: string;
  bodyHtml: string;
  isActive: boolean;
}

interface PreviewEnrollment {
  id: string;
  label: string;
  studentName: string;
  courseName: string;
}

export function ContractTemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [enrollmentId, setEnrollmentId] = useState('');
  const [enrollmentSearch, setEnrollmentSearch] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { data: template, isLoading } = useQuery({
    queryKey: ['contracts', 'templates', id],
    queryFn: () => api<TemplateDetail>(`/contracts/templates/${id}`),
    enabled: Boolean(id),
  });

  const { data: variablesData } = useQuery({
    queryKey: ['contracts', 'merge-variables'],
    queryFn: () => api<{ data: MergeVariable[] }>('/contracts/merge-variables'),
  });

  const debouncedEnrollmentSearch = useDebouncedValue(enrollmentSearch, 300);
  const { data: enrollmentsData, isLoading: enrollmentsLoading } = useQuery({
    queryKey: ['contracts', 'preview-enrollments', debouncedEnrollmentSearch],
    queryFn: () =>
      api<{ data: PreviewEnrollment[] }>(
        `/contracts/preview-enrollments?search=${encodeURIComponent(debouncedEnrollmentSearch.trim())}`,
      ),
  });

  useEffect(() => {
    if (!template) return;
    setTitle(template.title);
    setBodyHtml(template.bodyHtml);
    setIsActive(template.isActive);
  }, [template]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api(`/contracts/templates/${id}`, {
        method: 'PATCH',
        body: { title, bodyHtml, isActive },
      }),
    onSuccess: () => {
      toast(contractTemplateSuccessToast('edit'));
      void queryClient.invalidateQueries({ queryKey: ['contracts', 'templates'] });
      void queryClient.invalidateQueries({ queryKey: ['contracts', 'templates', id] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo guardar la plantilla'));
    },
  });

  const previewMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch(`/api/contracts/templates/${id}/preview/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enrollmentId, title, bodyHtml }),
      });
      if (!response.ok) {
        const text = await response.text();
        let message = 'No se pudo generar la vista previa.';
        try {
          const data = JSON.parse(text) as { message?: string };
          if (data.message) message = data.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      return response.blob();
    },
    onSuccess: (blob) => {
      setPreviewError(null);
      const url = URL.createObjectURL(blob);
      const previewTab = window.open(url, '_blank', 'noopener,noreferrer');
      if (!previewTab) {
        URL.revokeObjectURL(url);
        setPreviewError('Permite ventanas emergentes para abrir la vista previa en PDF.');
        return;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (error) => {
      setPreviewError(error instanceof Error ? error.message : 'No se pudo generar la vista previa.');
    },
  });

  const variables = variablesData?.data ?? [];
  const enrollments = enrollmentsData?.data ?? [];

  const variableGroups = useMemo(() => {
    const groups = new Map<string, MergeVariable[]>();
    for (const variable of variables) {
      const groupName = variable.group ?? 'General';
      if (!groups.has(groupName)) groups.set(groupName, []);
      groups.get(groupName)!.push(variable);
    }
    return [...groups.entries()];
  }, [variables]);

  const hasCustomFields = variables.some((variable) => variable.key.startsWith('alumno.campo.'));

  const logoSnippet =
    '<img src="{{sistema.logo}}" alt="Logo" style="max-width:200px;height:auto;display:block;margin:0 auto 1rem" />\n\n';
  const mediaImageSnippet =
    '<img src="/api/media/files/ID_DEL_ARCHIVO/content" alt="Imagen" style="max-width:200px;height:auto;display:block;margin:0 auto 1rem" />\n\n';
  const logoRightSnippet =
    '<img src="{{sistema.logo}}" alt="Logo" style="max-width:140px;height:auto;float:right;margin:0 0 1rem 1rem" />\n\n';
  const logoLeftSnippet =
    '<img src="{{sistema.logo}}" alt="Logo" style="max-width:140px;height:auto;float:left;margin:0 1rem 1rem 0" />\n\n';
  const legalSignatureSnippet =
    '<img src="{{sistema.firma_representante}}" alt="Firma representante legal" style="max-width:100%;max-height:90px;height:auto;display:block;margin:0 auto" />\n';
  const signaturesTwoColumnSnippet =
    '<p><strong>FIRMA Y ACEPTACIÓN</strong></p>\n<table style="width:100%;border-collapse:collapse">\n' +
    '  <tr><td style="width:50%;vertical-align:top"><strong>Alumno</strong></td><td style="width:50%;vertical-align:top"><strong>Prestador del servicio</strong></td></tr>\n' +
    '  <tr><td style="width:50%;vertical-align:top">Nombre: {{alumno.nombre_completo}}</td><td style="width:50%;vertical-align:top">Nombre:</td></tr>\n' +
    '  <tr><td style="width:50%;vertical-align:top">DNI/RUT: {{alumno.campo.dni}}</td><td style="width:50%;vertical-align:top">RUT:</td></tr>\n' +
    '  <tr><td style="width:50%;vertical-align:top"><div class="contract-signature-space"></div></td><td style="width:50%;vertical-align:top;text-align:center">' +
    legalSignatureSnippet +
    '</td></tr>\n' +
    '  <tr><td style="width:50%;vertical-align:top">Firma: ___________________________<br>Fecha: {{sistema.fecha}}</td><td style="width:50%;vertical-align:top">Firma: ___________________________<br>Fecha: {{sistema.fecha}}</td></tr>\n' +
    '</table>\n\n';

  if (isLoading || !template) {
    return (
      <div className="lms-contract-templates lms-site-admin-hub-page font-sans">
        <Loader label="Cargando editor…" />
      </div>
    );
  }

  return (
    <div className="lms-contract-templates lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/contratos/plantillas" label="Volver a Plantillas de contrato" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Editor de plantilla</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              {title.trim() ? title : 'HTML con variables de merge para contratos de servicios.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-dash-primary px-6 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 disabled:opacity-60"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
            save
          </span>
          {saveMutation.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <div className="lms-credentials-glass-panel space-y-4 rounded-2xl p-6 shadow-sm">
          <label className="block text-sm font-medium text-dash-on-surface" htmlFor="template-title-edit">
            Título
          </label>
          <input
            id="template-title-edit"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-xl border border-dash-outline-variant px-4 py-3 outline-none transition-all focus:border-dash-secondary focus:ring-1 focus:ring-dash-secondary"
          />

          <label className="block text-sm font-medium text-dash-on-surface" htmlFor="template-body">
            Contenido HTML
          </label>
          <textarea
            id="template-body"
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            rows={18}
            className="w-full rounded-xl border border-dash-outline-variant px-4 py-3 font-mono text-sm outline-none transition-all focus:border-dash-secondary focus:ring-1 focus:ring-dash-secondary"
          />

          <label className="flex items-center gap-2 text-sm text-dash-on-surface">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Plantilla activa (genera contratos al matricular)
          </label>

          <div className="rounded-xl border border-dash-outline-variant bg-white/60 p-4">
            <h2 className="font-semibold text-dash-on-surface">Vista previa</h2>
            <p className="mt-1 text-sm text-dash-on-surface-variant">
              Elige una matrícula y se abrirá la vista previa en PDF en una nueva pestaña.
            </p>
            <div className="mt-3 space-y-3">
              <input
                value={enrollmentSearch}
                onChange={(e) => setEnrollmentSearch(e.target.value)}
                placeholder="Buscar por alumno o curso…"
                className="w-full rounded-xl border border-dash-outline-variant px-4 py-2 text-sm outline-none transition-all focus:border-dash-secondary focus:ring-1 focus:ring-dash-secondary"
              />
              <div className="flex flex-wrap gap-2">
                <select
                  value={enrollmentId}
                  onChange={(e) => {
                    setEnrollmentId(e.target.value);
                    setPreviewError(null);
                  }}
                  className="min-w-[220px] flex-1 rounded-xl border border-dash-outline-variant bg-white px-4 py-2 text-sm outline-none transition-all focus:border-dash-secondary focus:ring-1 focus:ring-dash-secondary"
                  disabled={enrollmentsLoading}
                >
                  <option value="">
                    {enrollmentsLoading ? 'Cargando matrículas…' : 'Seleccionar matrícula…'}
                  </option>
                  {enrollments.map((enrollment) => (
                    <option key={enrollment.id} value={enrollment.id}>
                      {enrollment.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-dash-primary px-4 py-2 text-sm font-medium text-dash-primary transition-colors hover:bg-brand-50 disabled:opacity-60"
                  disabled={!enrollmentId || previewMutation.isPending}
                  onClick={() => previewMutation.mutate()}
                >
                  Previsualizar
                </button>
              </div>
              {!enrollmentsLoading && enrollments.length === 0 ? (
                <p className="text-xs text-dash-on-surface-variant">
                  No hay matrículas con curso. Matricula un alumno primero para probar la plantilla.
                </p>
              ) : null}
              {previewError ? <p className="text-sm text-red-700">{previewError}</p> : null}
            </div>
          </div>
        </div>

        <aside className="lms-credentials-glass-panel h-fit max-h-[calc(100vh-12rem)] overflow-y-auto rounded-2xl p-4 shadow-sm">
          <h2 className="font-semibold text-dash-on-surface">Variables disponibles</h2>
          <p className="mt-1 text-xs text-dash-on-surface-variant">
            Pulsa una variable para insertarla en el HTML.
          </p>
          <div className="mt-4 space-y-5">
            {variableGroups.map(([groupName, groupVariables]) => (
              <section key={groupName}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-dash-on-surface-variant">
                  {groupName}
                </h3>
                <ul className="mt-2 space-y-2 text-sm">
                  {groupVariables.map((variable) => (
                    <li key={variable.key}>
                      <button
                        type="button"
                        className="w-full rounded-lg border border-dash-outline-variant bg-white/60 px-3 py-2 text-left transition-colors hover:bg-white"
                        onClick={() => setBodyHtml((prev) => `${prev}${variable.example}`)}
                      >
                        <span className="font-mono text-xs text-brand-800">{variable.example}</span>
                        <span className="mt-1 block text-dash-on-surface-variant">{variable.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-dash-on-surface-variant">
                Imágenes
              </h3>
              <p className="mt-1 text-xs text-dash-on-surface-variant">
                Sube logo y firma en <strong>Assets del sitio</strong> (<code className="text-brand-800">/apariencia</code>).
                Usa <code className="text-brand-800">{'{{sistema.logo}}'}</code> o{' '}
                <code className="text-brand-800">{'{{sistema.firma_representante}}'}</code>. Personaliza tamaño y
                posición con <code className="text-brand-800">style</code>.
              </p>
              <ul className="mt-2 space-y-2 text-sm">
                <li>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-dash-outline-variant bg-white/60 px-3 py-2 text-left transition-colors hover:bg-white"
                    onClick={() => setBodyHtml((prev) => `${logoSnippet}${prev}`)}
                  >
                    <span className="font-mono text-xs text-brand-800">Logo centrado</span>
                    <span className="mt-1 block text-dash-on-surface-variant">
                      max-width + margin: 0 auto
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-dash-outline-variant bg-white/60 px-3 py-2 text-left transition-colors hover:bg-white"
                    onClick={() => setBodyHtml((prev) => `${logoRightSnippet}${prev}`)}
                  >
                    <span className="font-mono text-xs text-brand-800">Logo a la derecha</span>
                    <span className="mt-1 block text-dash-on-surface-variant">float:right</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-dash-outline-variant bg-white/60 px-3 py-2 text-left transition-colors hover:bg-white"
                    onClick={() => setBodyHtml((prev) => `${logoLeftSnippet}${prev}`)}
                  >
                    <span className="font-mono text-xs text-brand-800">Logo a la izquierda</span>
                    <span className="mt-1 block text-dash-on-surface-variant">float:left</span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-dash-outline-variant bg-white/60 px-3 py-2 text-left transition-colors hover:bg-white"
                    onClick={() => setBodyHtml((prev) => `${mediaImageSnippet}${prev}`)}
                  >
                    <span className="font-mono text-xs text-brand-800">Imagen de multimedia</span>
                    <span className="mt-1 block text-dash-on-surface-variant">
                      `/api/media/files/ID/content` — alternativa manual
                    </span>
                  </button>
                </li>
              </ul>
            </section>
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-dash-on-surface-variant">
                Firmas
              </h3>
              <p className="mt-1 text-xs text-dash-on-surface-variant">
                Tabla de dos columnas: alumno a la izquierda, prestador a la derecha. Solo la columna del alumno usa{' '}
                <code className="text-brand-800">&lt;div class=&quot;contract-signature-space&quot;&gt;&lt;/div&gt;</code>{' '}
                (firma digital al aceptar). La del representante usa{' '}
                <code className="text-brand-800">{'{{sistema.firma_representante}}'}</code>. Ajusta el tamaño editando
                el <code className="text-brand-800">style</code> del <code className="text-brand-800">&lt;img&gt;</code>
                : <code className="text-brand-800">max-width</code> (px o %) y{' '}
                <code className="text-brand-800">max-height</code> (px).
              </p>
              <ul className="mt-2 space-y-2 text-sm">
                <li>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-dash-outline-variant bg-white/60 px-3 py-2 text-left transition-colors hover:bg-white"
                    onClick={() => setBodyHtml((prev) => `${prev}${signaturesTwoColumnSnippet}`)}
                  >
                    <span className="font-mono text-xs text-brand-800">Firmas en dos columnas</span>
                    <span className="mt-1 block text-dash-on-surface-variant">
                      Alumno (izq.) · Prestador (der.)
                    </span>
                  </button>
                </li>
              </ul>
            </section>
            {!hasCustomFields ? (
              <p className="text-xs text-dash-on-surface-variant">
                No hay campos personalizados configurados. Créalos en Administración → Campos de usuario.
              </p>
            ) : null}
          </div>
        </aside>
      </div>
    </div>
  );
}
